import { AlertTriangle, ArrowRight } from 'blaze-ui/icons'
import Styles from './useAutopilotPausedBanner.module.scss'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { useOpenSettings } from 'hooks/useOpenSettings'
import { SettingScreen } from 'components/Settings/types'
import dayjs from 'dayjs'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useAPMobileWeb } from 'hooks/useAPMobileWeb'
import { useIsGenerationPaused } from 'hooks/useAutopilotPausedBanner/useIsGenerationPaused'
import { useIsPublishingPaused } from 'hooks/useAutopilotPausedBanner/useIsPublishingPaused'

export const useAutopilotPausedBanner = () => {
  const workspace = useCurrentWorkspace()
  const { openSettings } = useOpenSettings()

  const { isIOSNative } = useResponsiveDesign()
  const isAPMobileWeb = useAPMobileWeb()

  // Helper function to check if generation is paused
  const isGenerationPaused = useIsGenerationPaused()

  // Helper function to check if publishing is paused
  const isPublishingPaused = useIsPublishingPaused()

  const hideBanner =
    isIOSNative ||
    isAPMobileWeb ||
    !workspace ||
    !workspace.is_virtual_marketer ||
    (!isGenerationPaused && !isPublishingPaused)

  const message = () => {
    if (!workspace) return ''

    const {
      pause_autopilot_generate_at,
      pause_autopilot_publish_at,
      resume_autopilot_generate_at,
      resume_autopilot_publish_at,
    } = workspace

    const generationPausedIndefinitely = !!pause_autopilot_generate_at
    const generationScheduledToResume =
      resume_autopilot_generate_at && dayjs(resume_autopilot_generate_at).isAfter(dayjs())
    const publishingPausedIndefinitely = !!pause_autopilot_publish_at
    const publishingScheduledToResume =
      resume_autopilot_publish_at && dayjs(resume_autopilot_publish_at).isAfter(dayjs())

    // Both paused indefinitely
    if (generationPausedIndefinitely && publishingPausedIndefinitely) {
      return 'Content generation and publishing are paused indefinitely.'
    }

    // Both scheduled to resume
    if (generationScheduledToResume && publishingScheduledToResume) {
      if (dayjs(resume_autopilot_generate_at).isSame(dayjs(resume_autopilot_publish_at))) {
        return `Content generation and publishing are paused until ${dayjs(resume_autopilot_generate_at).format(
          'MMM D, YYYY',
        )}.`
      }

      return `Content generation is paused until ${dayjs(resume_autopilot_generate_at).format(
        'MMM D, YYYY',
      )}. Publishing is paused until ${dayjs(resume_autopilot_publish_at).format('MMM D, YYYY')}.`
    }

    // Generation paused indefinitely, publishing scheduled to resume
    if (generationPausedIndefinitely && publishingScheduledToResume) {
      return `Content generation is paused indefinitely. Publishing is paused until ${dayjs(
        resume_autopilot_publish_at,
      ).format('MMM D, YYYY')}.`
    }

    // Publishing paused indefinitely, generation scheduled to resume
    if (publishingPausedIndefinitely && generationScheduledToResume) {
      return `Auto content generation is paused until ${dayjs(resume_autopilot_generate_at).format(
        'MMM D, YYYY',
      )}. Publishing is paused indefinitely.`
    }

    // Only generation paused indefinitely
    if (generationPausedIndefinitely) {
      return 'Auto content generation is turned off indefinitely.'
    }

    // Only generation scheduled to resume
    if (generationScheduledToResume) {
      return `Auto content generation is turned off until ${dayjs(resume_autopilot_generate_at).format('MMM D, YYYY')}.`
    }

    // Only publishing paused indefinitely
    if (publishingPausedIndefinitely) {
      return 'Auto publishing is turned off indefinitely.'
    }

    // Only publishing scheduled to resume
    if (publishingScheduledToResume) {
      return `Auto publishing is turned off until ${dayjs(resume_autopilot_publish_at).format('MMM D, YYYY')}.`
    }

    return ''
  }

  const autopilotPausedBanner = (
    <div className={Styles.container}>
      <AlertTriangle color="var(--yellow-70)" size={30} />
      <div className={Styles.message}>
        <Text variant="smallList">{message()}</Text>
      </div>
      <div className={Styles.buttonContainer}>
        <Button size="sm" onPress={() => openSettings(SettingScreen.General)} variant="secondary" endIcon={ArrowRight}>
          Turn On in Settings
        </Button>
      </div>
    </div>
  )

  return {
    showBanner: !hideBanner,
    autopilotPausedBanner,
    isPublishingPaused,
    isGenerationPaused,
    message: message(),
  }
}
